import React, { useEffect, useId, useRef } from "react";
import ConZoom from "./ConZoom";
import Glifo from "./Glifos";

// Diagrama animado (SVG) de como funciona un servicio o una arquitectura. Se arma solo a partir de datos:
//   patron  "lineal"  nodos en fila, cada paso une un nodo con el siguiente           (pasos = nodos - 1)
//           "abanico" origen -> centro -> varios destinos, y la respuesta de vuelta    (pasos = 3, nodos 4 a 6)
//           "ciclo"   nodos en circulo, el ultimo vuelve al primero (retroalimentacion) (pasos = nodos)
//           "libre"   nodos con posicion propia [etiqueta, glifo, x, y] y flechas [desde, hasta, paso] (arquitecturas de ejemplo)
//   nodos   [[etiqueta, glifo], ...]
//   grupo   [desde, hasta, "etiqueta"] (o grupos: [{ idx: [..], etiqueta }]) recuadro punteado (VPC, cluster, region...)
//   paso    indice del paso resaltado (los "paquetes" siguen animados todos, el activo se ve mas fuerte)
//   nodoSel indice del nodo a destacar con un anillo que late (cuando se elige un objeto)
//   ataque  { ruta: [indices de nodo], bloqueaEn: indice | null, origen?: {x,y,label} | false }  un atacante (en rojo) recorre esa ruta; si bloqueaEn es un nodo,
//           el ataque se detiene ahi (la defensa funciona); si es null llega hasta el ultimo nodo, que queda «comprometido». Sin origen: se dibuja
//           arriba del primer nodo de la ruta; con origen:false la ruta ya incluye un nodo atacante.
//   quitados  Set de indices de nodo (controles de seguridad «quitados»): se dibujan punteados y grises
//   fallo   indice de un nodo "caido": se pinta en rojo y el recorrido se corta ahi (o se reparte entre los que siguen sanos)
// La animacion usa SMIL (animateMotion): se pausa con pausado=true.
export const R = 34;
const W = 800, H = 340;
const VISTA = { lineal: "0 70 800 200", abanico: "0 0 800 340", ciclo: "0 0 800 340", libre: "0 0 800 340" };

// En los diagramas libres la vista se ajusta a donde estan las piezas (un diagrama de una sola fila no deja un gran vacio arriba y abajo)
export const vistaLibre = (pos, conRecuadros, conAtaque = false) => {
    if (!pos.length) return VISTA.libre;
    const ys = pos.map(p => p.y);
    const arriba = conAtaque ? 150 : conRecuadros ? 82 : 66, abajo = 100;
    const y1 = Math.min(...ys) - arriba, y2 = Math.min(H + 40, Math.max(...ys) + abajo);   // puede pasar un poco del alto base: las piezas de la fila de abajo traen su etiqueta debajo
    return `0 ${Math.round(y1)} ${W} ${Math.round(Math.max(y2 - y1, 150))}`;
};

export const pasosDe = (patron, n, flujosLibres) =>
    patron === "libre" ? Math.max(...(flujosLibres || [[0, 0, 0]]).map(f => f[2] || 0)) + 1 : patron === "abanico" ? 3 : patron === "ciclo" ? n : n - 1;

const unit = (a, b) => { const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy) || 1; return { x: dx / d, y: dy / d }; };
const mas = (p, v, k) => ({ x: p.x + v.x * k, y: p.y + v.y * k });
const M = (p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
const recto = (a, b) => { const u = unit(a, b); const p = mas(a, u, R), q = mas(b, u, -R); return { d: `M ${M(p)} L ${M(q)}`, fin: q, mid: { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 } }; };

// Posiciones de los nodos y tramos de cada paso: flujos[paso] = { retorno?, tramos: [{ d, u, v, fin, mid }] } (u -> v = indices de nodo)
export const disponer = (patron, n, nodos, flujosLibres) => {
    const pos = [];
    const flujos = [];
    if (patron === "libre") {
        (nodos || []).forEach(nd => pos.push({ x: nd[2], y: nd[3] }));
        const pasos = pasosDe("libre", n, flujosLibres);
        for (let i = 0; i < pasos; i++) flujos.push({ tramos: [] });
        (flujosLibres || []).forEach(([u, v, paso = 0]) => flujos[paso].tramos.push({ u, v, ...recto(pos[u], pos[v]) }));
    } else if (patron === "abanico") {
        const m = n - 2;
        pos.push({ x: 100, y: 160 }, { x: 330, y: 160 });
        const sep = m <= 3 ? 90 : 76;
        for (let j = 0; j < m; j++) pos.push({ x: 610, y: 160 + (j - (m - 1) / 2) * sep });
        flujos.push({ tramos: [{ u: 0, v: 1, ...recto(pos[0], pos[1]) }] });
        flujos.push({ tramos: pos.slice(2).map((p, j) => ({ u: 1, v: j + 2, ...recto(pos[1], p) })) });
        // la respuesta vuelve en un solo arco punteado por debajo de todo, desde el ultimo destino hasta el origen
        const ult = pos[pos.length - 1];
        const ini = { x: ult.x - 6, y: ult.y + R }, ctl = { x: 330, y: Math.min(H - 4, ult.y + R + 70) }, fin = { x: 100, y: 160 + R };
        flujos.push({ retorno: true, tramos: [{ u: n - 1, v: 0, d: `M ${M(ini)} Q ${M(ctl)} ${M(fin)}`, fin, mid: { x: (ini.x + ctl.x) / 2, y: (ini.y + ctl.y) / 2 } }] });
    } else if (patron === "ciclo") {
        const c = { x: 400, y: 165 };
        for (let i = 0; i < n; i++) { const ang = -Math.PI / 2 + (i * 2 * Math.PI) / n; pos.push({ x: c.x + 270 * Math.cos(ang), y: c.y + 105 * Math.sin(ang) }); }
        for (let i = 0; i < n; i++) {
            const a = pos[i], b = pos[(i + 1) % n];
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const ctrl = { x: c.x + (mid.x - c.x) * 1.3, y: c.y + (mid.y - c.y) * 1.3 };
            const p = mas(a, unit(a, ctrl), R), q = mas(b, unit(b, ctrl), R);
            flujos.push({ tramos: [{ u: i, v: (i + 1) % n, d: `M ${M(p)} Q ${M(ctrl)} ${M(q)}`, fin: q, mid: { x: (p.x + 2 * ctrl.x + q.x) / 4, y: (p.y + 2 * ctrl.y + q.y) / 4 } }] });
        }
    } else {
        for (let i = 0; i < n; i++) pos.push({ x: n === 1 ? 400 : 100 + (i * 600) / (n - 1), y: 160 });
        for (let i = 0; i < n - 1; i++) flujos.push({ tramos: [{ u: i, v: i + 1, ...recto(pos[i], pos[i + 1]) }] });
    }
    return { pos, flujos };
};

// Simula una falla: desde los nodos de origen se recorre el diagrama sin pasar por el nodo caido. Devuelve los nodos que siguen "vivos" y
// que tramos siguen activos (los que no, se dibujan apagados y sin paquetes).
export const propagar = (flujos, n, fallo) => {
    if (fallo == null) return { vivos: new Set(Array.from({ length: n }, (_, i) => i)), activo: () => true, hayCortes: false };
    const ida = flujos.filter(f => !f.retorno).flatMap(f => f.tramos);
    const entran = new Set(ida.map(t => t.v));
    let origenes = Array.from({ length: n }, (_, i) => i).filter(i => !entran.has(i));
    if (!origenes.length) origenes = [0];
    const vivos = new Set(origenes.filter(i => i !== fallo));
    for (let pasada = 0; pasada < n + 1; pasada++) ida.forEach(t => { if (vivos.has(t.u) && t.v !== fallo) vivos.add(t.v); });
    const activo = (t) => vivos.has(t.u) && vivos.has(t.v) && t.u !== fallo && t.v !== fallo;
    return { vivos, activo, hayCortes: flujos.some(f => f.tramos.some(t => !activo(t))) };
};

// Texto en hasta 2 lineas
const lineas = (t, max = 15) => {
    const palabras = String(t).split(" ");
    const out = [""];
    palabras.forEach(p => {
        const ult = out[out.length - 1];
        if (!ult) out[out.length - 1] = p;
        else if ((ult + " " + p).length <= max || out.length >= 2) out[out.length - 1] = ult + " " + p;
        else out.push(p);
    });
    return out;
};

const ROJO = "#dc3545";

const Diagrama = ({ patron = "lineal", nodos, flujosLibres, grupo, grupos, color = "#ED7100", paso = 0, nodoSel = null, fallo = null, ataque = null, quitados = null, pausado = false, onNodo }) => {
    const uid = useId().replace(/[^a-zA-Z0-9]/g, "_");
    const ref = useRef(null);
    useEffect(() => {
        const s = ref.current;
        if (!s) return;
        if (pausado && s.pauseAnimations) s.pauseAnimations();
        if (!pausado && s.unpauseAnimations) s.unpauseAnimations();
    }, [pausado, patron, nodos]);

    const { pos, flujos } = disponer(patron, nodos.length, nodos, flujosLibres);
    const sim = propagar(flujos, nodos.length, fallo);
    const activoPaso = flujos[paso] || flujos[0] || { tramos: [] };
    const extremos = new Set(activoPaso.tramos.flatMap(t => [t.u, t.v]));

    // Ataque: puntos del recorrido (origen -> nodos de la ruta) y hasta donde llega
    let tramosAtaque = [], origenAt = null, hastaIdx = -1;
    if (ataque && ataque.ruta && ataque.ruta.length) {
        const ruta = ataque.ruta;
        hastaIdx = ataque.bloqueaEn != null ? ruta.indexOf(ataque.bloqueaEn) : ruta.length - 1;
        if (hastaIdx < 0) hastaIdx = ruta.length - 1;
        const ys0 = pos.map(q => q.y);
        origenAt = ataque.origen === false ? null : (ataque.origen || { x: pos[ruta[0]].x, y: Math.min(...ys0) - 112, label: "Atacante" });
        const puntos = [...(origenAt ? [{ x: origenAt.x, y: origenAt.y, esOrigen: true }] : []), ...ruta.slice(0, hastaIdx + 1).map(i => ({ ...pos[i], i }))];
        for (let k = 0; k < puntos.length - 1; k++) {
            const a = puntos[k], b = puntos[k + 1];
            const u = unit(a, b);
            const p = mas(a, u, a.esOrigen ? 30 : R), q = mas(b, u, -R);
            tramosAtaque.push({ d: `M ${M(p)} L ${M(q)}`, k });
        }
    }
    const listaGrupos = grupos || (grupo ? [{ idx: Array.from({ length: grupo[1] - grupo[0] + 1 }, (_, k) => grupo[0] + k), etiqueta: grupo[2] }] : []);
    const cajas = listaGrupos.map((g, k) => {
        const sel = g.idx.map(i => pos[i]);
        const x1 = Math.min(...sel.map(p => p.x)) - R - 26, x2 = Math.max(...sel.map(p => p.x)) + R + 26;
        const y1 = Math.min(...sel.map(p => p.y)) - R - 34, y2 = Math.max(...sel.map(p => p.y)) + R + 46;
        return (
            <g key={k}>
                <rect x={x1} y={y1} width={x2 - x1} height={y2 - y1} rx="14" fill={color} fillOpacity="0.07" stroke={color} strokeOpacity="0.7" strokeDasharray="7 5" strokeWidth="1.6" />
                <text x={x1 + 12} y={y1 + 18} fontSize="12" fontWeight="700" fill={color}>{g.etiqueta}</text>
            </g>
        );
    });

    return (
        <ConZoom>
        <svg ref={ref} viewBox={patron === "libre" ? vistaLibre(pos, listaGrupos.length > 0, !!(origenAt && ataque.origen !== false)) : (VISTA[patron] || VISTA.lineal)} width="100%" role="img" style={{ maxHeight: 380, display: "block" }}
            aria-label={`Diagrama: ${nodos.map(n => n[0]).join(", ")}${fallo != null ? `. Simulación: falla «${nodos[fallo][0]}»` : ""}${ataque ? `. Simulación de ataque: ${ataque.bloqueaEn != null ? "bloqueado por una defensa" : "la defensa falta y el ataque llega a su objetivo"}` : ""}`}>
            <defs>
                <marker id={`${uid}f`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill={color} /></marker>
                <marker id={`${uid}r`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill={ROJO} /></marker>
                <marker id={`${uid}g`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="#9aa0a6" /></marker>
            </defs>
            {cajas}
            {flujos.map((f, i) => {
                const on = i === paso;
                const hermanoCaido = f.tramos.some(t => !sim.activo(t));   // si otro tramo del mismo paso esta cortado, los que siguen sanos absorben el trafico
                return (
                    <g key={i}>
                        {f.tramos.map((t, k) => {
                            const vivo = sim.activo(t);
                            return (
                                <g key={k}>
                                    <path d={t.d} fill="none" stroke={vivo ? color : "#9aa0a6"} strokeWidth={vivo && on ? 3.2 : 1.6} strokeOpacity={vivo ? (on ? 1 : 0.4) : 0.6}
                                        strokeDasharray={!vivo ? "3 6" : f.retorno ? "6 5" : undefined} markerEnd={`url(#${uid}${vivo ? "f" : "g"})`} />
                                    {vivo && (hermanoCaido && !f.retorno ? [0, 1, 2] : [0, 1]).map(q => (
                                        <circle key={q} r={on ? 6 : 3.5} fill={color} fillOpacity={on ? 1 : 0.35} visibility="hidden">
                                            {/* oculta hasta que empieza su recorrido: antes de "begin" SMIL la dibujaria en la esquina (0,0) */}
                                            <set attributeName="visibility" to="visible" begin={`${q * 0.95}s`} />
                                            <animateMotion dur="2.8s" begin={`${q * 0.95}s`} repeatCount="indefinite" path={t.d} />
                                        </circle>
                                    ))}
                                    {!vivo && t.v === fallo && (
                                        <text x={t.fin.x - 6} y={t.fin.y + 6} fontSize="22" fontWeight="900" fill={ROJO} textAnchor="middle">×</text>
                                    )}
                                </g>
                            );
                        })}
                        {f.tramos.length > 0 && (
                            <text fontSize="12" fontWeight="700" fill={color} fillOpacity={on ? 1 : 0.55} x={f.tramos[0].mid.x} y={f.tramos[0].mid.y - 10} textAnchor="middle">{i + 1}</text>
                        )}
                    </g>
                );
            })}
            {nodos.map((n, i) => {
                const p = pos[i];
                const lin = lineas(n[0]);
                const lado = patron === "abanico" && i >= 2;    // en el abanico los destinos van uno sobre otro: la etiqueta va a su derecha
                const caido = fallo === i;
                const quitado = !!(quitados && quitados.has(i));
                const apagado = fallo != null && !sim.vivos.has(i) && !caido;   // nodos que quedan sin recibir nada por la falla
                const c = caido ? ROJO : apagado || quitado ? "#9aa0a6" : color;
                return (
                    <g key={i} style={{ cursor: onNodo ? "pointer" : "default" }} onClick={onNodo ? () => onNodo(i) : undefined}>
                        {nodoSel === i && (
                            <circle cx={p.x} cy={p.y} r={R + 6} fill="none" stroke={color} strokeWidth="3">
                                <animate attributeName="r" values={`${R + 4};${R + 13};${R + 4}`} dur="1.6s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="1;0.25;1" dur="1.6s" repeatCount="indefinite" />
                            </circle>
                        )}
                        <circle cx={p.x} cy={p.y} r={R} fill={caido ? ROJO : extremos.has(i) && !apagado ? color : "#fff"} fillOpacity={caido ? 0.12 : extremos.has(i) && !apagado ? 0.14 : 1}
                            stroke={c} strokeWidth={extremos.has(i) || nodoSel === i || caido ? 3.5 : 2} strokeDasharray={apagado || quitado ? "4 4" : undefined} />
                        <Glifo g={n[1]} x={p.x} y={p.y} color={c} escala={1.05} />
                        {caido && <path d={`M ${p.x - 17} ${p.y - 17} L ${p.x + 17} ${p.y + 17} M ${p.x + 17} ${p.y - 17} L ${p.x - 17} ${p.y + 17}`} stroke={ROJO} strokeWidth="4" strokeLinecap="round" />}
                        <text x={lado ? p.x + R + 10 : p.x} y={lado ? p.y + 5 - (lin.length - 1) * 7.5 : p.y + R + 17} textAnchor={lado ? "start" : "middle"} fontSize="13" fontWeight="600"
                            fill={caido ? ROJO : "currentColor"} fillOpacity={apagado || quitado ? 0.55 : 1}>
                            {quitado && <tspan x={lado ? p.x + R + 10 : p.x} dy="-15" fontSize="11" fontWeight="800" fill={ROJO}>QUITADO</tspan>}
                            {lin.map((l, k) => <tspan key={k} x={lado ? p.x + R + 10 : p.x} dy={k === 0 ? (quitado ? 15 : 0) : 15}>{l}</tspan>)}
                        </text>
                    </g>
                );
            })}
            {origenAt && (
                <g>
                    <circle cx={origenAt.x} cy={origenAt.y} r="28" fill="#fff" stroke={ROJO} strokeWidth="3" />
                    <Glifo g="usuario" x={origenAt.x} y={origenAt.y} color={ROJO} escala={0.95} />
                    <text x={origenAt.x} y={origenAt.y + 28 + 15} textAnchor="middle" fontSize="12" fontWeight="800" fill={ROJO}>{origenAt.label || "Atacante"}</text>
                </g>
            )}
            {tramosAtaque.map(t => (
                <g key={`at${t.k}`}>
                    <path d={t.d} fill="none" stroke={ROJO} strokeWidth="3" strokeDasharray="8 5" markerEnd={`url(#${uid}r)`} />
                    {[0, 1].map(q => (
                        <circle key={q} r="5.5" fill={ROJO} visibility="hidden">
                            <set attributeName="visibility" to="visible" begin={`${t.k * 0.5 + q * 1.1}s`} />
                            <animateMotion dur="2.2s" begin={`${t.k * 0.5 + q * 1.1}s`} repeatCount="indefinite" path={t.d} />
                        </circle>
                    ))}
                </g>
            ))}
            {ataque && hastaIdx >= 0 && (() => {
                const nodoFin = ataque.ruta[hastaIdx], q = pos[nodoFin];
                const bloqueado = ataque.bloqueaEn != null;
                return (
                    <g>
                        {bloqueado
                            ? <circle cx={q.x} cy={q.y} r={R + 7} fill="none" stroke="#198754" strokeWidth="4" strokeOpacity="0.9"><animate attributeName="stroke-opacity" values="1;0.35;1" dur="1.6s" repeatCount="indefinite" /></circle>
                            : <circle cx={q.x} cy={q.y} r={R + 7} fill="none" stroke={ROJO} strokeWidth="4"><animate attributeName="r" values={`${R + 5};${R + 14};${R + 5}`} dur="1.2s" repeatCount="indefinite" /><animate attributeName="opacity" values="1;0.3;1" dur="1.2s" repeatCount="indefinite" /></circle>}
                        <circle cx={q.x + 25} cy={q.y - 25} r="12" fill={bloqueado ? "#198754" : ROJO} />
                        <text x={q.x + 25} y={q.y - 20} textAnchor="middle" fontSize="15" fontWeight="900" fill="#fff">{bloqueado ? "✓" : "!"}</text>
                        <text x={q.x} y={q.y - R - 12} textAnchor="middle" fontSize="12" fontWeight="800" fill={bloqueado ? "#198754" : ROJO}>{bloqueado ? "BLOQUEADO" : "COMPROMETIDO"}</text>
                    </g>
                );
            })()}
        </svg>
        </ConZoom>
    );
};

export default Diagrama;
